import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
  LuArrowRight, LuEye, LuLock, LuCircleCheck, LuUserX, LuServerOff, LuZap,
  LuArrowLeftRight, LuWandSparkles, LuShieldCheck,
  LuFileSpreadsheet, LuTable, LuBraces, LuExternalLink,
} from "react-icons/lu";
import SeoHead from "../components/SeoHead";
import { pageMeta } from "../data/pageMeta";
import TopBanner from "../components/TopBanner";
import { articles } from "./blog/articles";

// Texte bewusst unverändert zur bisherigen Seite (SEO)
const TOOL_CARDS = [
  { to: "/edi-konverter", icon: LuArrowLeftRight, title: "EDI Konverter", text: "EDIFACT → Excel, CSV, XML, JSON", more: "Drag & Drop, sofortiger Download" },
  { to: "/edifact-viewer", icon: LuEye, title: "EDI Viewer", text: "EDIFACT-Dateien anzeigen & analysieren", more: "Segmente ansehen und verstehen" },
  { to: "/edifact-formatter", icon: LuWandSparkles, title: "EDI Formatter", text: "EDIFACT formatieren & bearbeiten", more: "Lesbar machen, Syntax prüfen" },
  { to: "/edifact-validator", icon: LuShieldCheck, title: "EDI Validator", text: "EDIFACT auf Fehler prüfen", more: "Syntax, Struktur & typische Fehler" },
];

const FEATURES = [
  { icon: LuUserX, title: "Keine Registrierung", text: "Öffnen, konvertieren, schließen. Keine Accounts, keine Werbung, kein Tracking." },
  { icon: LuServerOff, title: "Keine Serverübertragung", text: "Verarbeitung erfolgt lokal im Browser (kein Server-Upload). Bitte nur für nicht vertrauliche Testdaten verwenden." },
  { icon: LuZap, title: "Sofort-Download", text: "Drag & Drop → Vorschau → 1 Klick für Excel, CSV, XML oder JSON." },
];

const FORMAT_LINKS = [
  { to: "/edifact-csv-konverter", icon: LuTable, title: "EDIFACT → CSV Konverter", text: "ideal für ERP‑Importe" },
  { to: "/edifact-excel-konverter", icon: LuFileSpreadsheet, title: "EDIFACT → Excel (.xlsx)", text: "schnelle Tabellen­ansicht" },
  { to: "/edifact-xml-konverter", icon: LuBraces, title: "EDIFACT → XML/JSON", text: "für Entwickler‑Workflows" },
];

// Führende Emojis aus Teasertexten nur optisch entfernen
const cleanExcerpt = (s = "") => s.replace(/^[^\p{L}\p{N}]+/u, "");

export default function Landing() {
  const videoRef = useRef(null);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          v.play().catch(() => {});
        } else {
          v.pause();
        }
      },
      { threshold: 0.4 }
    );
    io.observe(v);
    return () => io.disconnect();
  }, []);

  return (
    <>
      <SeoHead
        {...pageMeta["/"]}
        ogUrl="https://edi-converter.de/"
      />

      <TopBanner />

      {/* ===== Hero ===== */}
      <section className="hero">
        {/* Reihenfolge Titel → Bild → Text: am Handy steht das GIF so direkt unter der Überschrift */}
        <div className="container hero-grid hero-home">
          <div className="hero-head">
            <h1 className="display">EDI Konverter</h1>
            <p className="display-sub">
              EDIFACT zu Excel, CSV, XML &amp; JSON{" "}
              <span className="display-sub-small">direkt im Browser umwandeln</span>
            </p>
          </div>

          <div className="mock hero-media">
            <div className="mock-bar" aria-hidden="true">
              <span className="mock-dot" /><span className="mock-dot" /><span className="mock-dot" />
              <span className="mock-url"><LuLock size={11} />edi-converter.de</span>
            </div>
            <img
              src="/demo-poster.gif"
              alt="Screenshot EDI-Konvertierung im Browser"
              width="1200"
              height="600"
              style={{ display: "block", width: "100%", height: "auto", background: "#fff" }}
            />
          </div>

          <div className="hero-body">
            <p className="lead">
              EDIFACT-Dateien sekundenschnell in <b>Excel, CSV, XML oder JSON</b> – 100 % <b>lokal im Browser</b>, ohne Installation.
            </p>
            <div className="hero-actions">
              <Link to="/edi-konverter" className="btn btn-primary btn-lg">
                Datei konvertieren <LuArrowRight size={18} aria-hidden="true" />
              </Link>
              <Link to="/edifact-viewer" className="btn btn-secondary btn-lg">
                <LuEye size={18} aria-hidden="true" /> Datei ansehen
              </Link>
            </div>
            <div className="hero-trust">
              <span><LuCircleCheck size={16} aria-hidden="true" />Keine Registrierung</span>
              <span><LuCircleCheck size={16} aria-hidden="true" />Keine Installation</span>
              <span><LuCircleCheck size={16} aria-hidden="true" />Kostenlos</span>
            </div>
          </div>
        </div>
      </section>

      {/* ===== Tools ===== */}
      <section className="section">
        <div className="container">
          <div className="grid grid-4 tool-cards">
            {TOOL_CARDS.map(t => (
              <Link key={t.to} to={t.to} className="card card-pad card-link">
                <span className="icon-tile"><t.icon size={20} aria-hidden="true" /></span>
                <h3 className="h3" style={{ marginTop: 18 }}>{t.title}</h3>
                <p className="muted small" style={{ marginTop: 6, flex: 1 }}>{t.text}</p>
                <span className="card-more">
                  {t.more}&nbsp;<LuArrowRight className="card-link-arrow" size={15} aria-hidden="true" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Demo-Video ===== */}
      <section className="section section-subtle">
        <div className="container">
          <div className="hero-grid" style={{ gap: 56 }}>
            <div>
              <h2 className="h2">Jetzt EDI File per<br />Drag and Drop konvertieren</h2>
              <p className="lead" style={{ marginTop: 12 }}>
                Keine Registrierung. Keine Installation. Einfach hineinziehen &amp; fertig.
              </p>
              <Link to="/edi-konverter" className="btn btn-primary" style={{ marginTop: 28 }}>
                Datei konvertieren <LuArrowRight size={16} aria-hidden="true" />
              </Link>
            </div>
            <div className="video-frame">
              <video
                ref={videoRef}
                src="/demo.mp4"
                poster="/demo-frame.jpg"
                muted
                loop
                playsInline
                preload="none"
                aria-label="Screen-Capture: Drag & Drop, Vorschau, Excel-Export in wenigen Sekunden."
              />
            </div>
          </div>
        </div>
      </section>

      {/* ===== Vorteile ===== */}
      <section className="section">
        <div className="container">
          <div className="grid grid-3">
            {FEATURES.map(f => (
              <article key={f.title} className="card card-pad">
                <span className="icon-tile"><f.icon size={20} aria-hidden="true" /></span>
                <h3 className="h3" style={{ marginTop: 18 }}>{f.title}</h3>
                <p className="muted" style={{ marginTop: 6 }}>{f.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Blog ===== */}
      <section className="section section-subtle">
        <div className="container">
          <div className="row" style={{ justifyContent: "space-between", alignItems: "flex-end", marginBottom: 32 }}>
            <h2 className="h2">Neu im Blog</h2>
            <Link to="/blog" className="btn btn-secondary btn-sm">
              Zu allen Blog-Artikeln <LuArrowRight size={15} aria-hidden="true" />
            </Link>
          </div>
          <div className="grid grid-3">
            {articles.slice(0, 3).map(article => (
              <Link key={article.slug} to={`/blog/${article.slug}`} className="card card-link">
                <div className="card-link-overflow">
                  <img src={article.image} alt={article.title} className="blog-card-img" loading="lazy" />
                </div>
                <div className="blog-card-body">
                  <div className="blog-card-title">{article.title}</div>
                  <div className="blog-card-excerpt">{cleanExcerpt(article.excerpt)}</div>
                  <span className="blog-card-more">Weiterlesen <LuArrowRight className="card-link-arrow" size={15} aria-hidden="true" /></span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Was ist EDIFACT ===== */}
      <section className="section">
        <div className="container container-md">
          <div className="section-head" style={{ maxWidth: 720 }}>
            <h2 className="h2">Was ist EDIFACT und wie wandelst du es um?</h2>
            <p className="lead">
              <strong className="strong">EDIFACT</strong> (Electronic Data Interchange For Administration, Commerce and Transport)
              ist der führende <em>EDI‑Standard</em> für Bestellungen, Rechnungen und Liefer­avise.
              Mit unserem <strong className="strong">EDI Konverter</strong> kannst du diese Dateien <strong className="strong">offline im Browser</strong> prüfen
              und in gängige Formate exportieren – ganz ohne Serverrisiko.
            </p>
          </div>

          <div className="grid grid-3">
            {FORMAT_LINKS.map(f => (
              <Link key={f.to} to={f.to} className="card card-pad card-link" style={{ flexDirection: "row", alignItems: "center", gap: 14 }}>
                <span className="icon-tile"><f.icon size={20} aria-hidden="true" /></span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span className="strong" style={{ display: "block", fontSize: 15 }}>{f.title}</span>
                  <span className="muted small">{f.text}</span>
                </span>
                <LuArrowRight className="card-link-arrow" size={16} aria-hidden="true" />
              </Link>
            ))}
          </div>

          <div className="cta-band" style={{ marginTop: 56 }}>
            <p className="cta-band-text">Keine Registrierung. Keine Installation. Einfach hineinziehen &amp; fertig.</p>
            <div className="row">
              <Link to="/edi-konverter" className="btn btn-secondary btn-lg">
                EDIFACT-Datei jetzt umwandeln
              </Link>
              <a
                href="https://de.wikipedia.org/wiki/EDIFACT"
                target="_blank"
                rel="noopener noreferrer"
                className="link"
                style={{ color: "rgba(255,255,255,.85)" }}
              >
                EDIFACT (Wikipedia) <LuExternalLink size={14} aria-hidden="true" />
              </a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
